import Icon from '../Icon';

/**
 * Разделы админки — меню для телефона.
 *
 * На широком экране этих пунктов здесь нет: они стоят вложенным списком в
 * общем боковом меню приложения (`App.tsx`). Своя колонка разделов рядом с
 * ним давала два меню бок о бок — и читалась как ошибка вёрстки, потому
 * что ею и была.
 *
 * На телефоне бокового меню нет вовсе, там внизу вкладки. Поэтому список
 * разделов становится отдельным экраном, как в системных настройках:
 * выбрал раздел — открылся, «назад» вернуло к списку.
 *
 * Порядок и группировка (`ADMIN_GROUPS`) общие с боковым меню: привыкший
 * к вебу должен узнавать телефон.
 */

export type AdminTab =
  | 'users'
  | 'companies'
  | 'cargos'
  | 'groups'
  | 'rules'
  | 'features'
  | 'broadcast'
  | 'settings'
  | 'log'
  | 'currencies'
  | 'integrations'
  | 'ingest';

export const ADMIN_TAB_LABEL: Record<AdminTab, string> = {
  users: 'Пользователи',
  companies: 'Компании',
  cargos: 'Заявки',
  broadcast: 'Рассылки',
  groups: 'Группы',
  rules: 'Контракты',
  features: 'Функции',
  settings: 'Настройки',
  log: 'Журнал',
  currencies: 'Валюты',
  integrations: 'Интеграции',
  ingest: 'Импорт из чатов',
};

/**
 * Группы разделов.
 *
 * Десять пунктов подряд читаются как свалка. Разбивка по смыслу: кем
 * управляем, что происходит, по каким правилам, чем управляется сама
 * площадка. Порядок внутри — по частоте обращения, а не по алфавиту.
 */
export const ADMIN_GROUPS: Array<{ title: string; items: AdminTab[] }> = [
  { title: 'Участники', items: ['users', 'companies'] },
  { title: 'Работа площадки', items: ['cargos', 'ingest', 'log', 'broadcast'] },
  { title: 'Правила и деньги', items: ['rules', 'currencies', 'groups'] },
  { title: 'Настройки', items: ['features', 'settings', 'integrations'] },
];

/** Короткая подпись: зачем сюда заходят. Только в меню на телефоне. */
const HINT: Record<AdminTab, string> = {
  users: 'Роли, доступ, пароли, бан',
  companies: 'Состав, владелец, проверка',
  cargos: 'Все заявки площадки',
  broadcast: 'Сообщения участникам',
  groups: 'Кому какие грузы видны',
  rules: 'Контракты и наценка',
  features: 'Что включено участникам',
  settings: 'Поддержка, режимы, модерация',
  log: 'Кто и что делал',
  currencies: 'Валюты и курсы',
  integrations: 'Токены и ключи подключений',
  ingest: 'Чаты логистов → заявки',
};

export default function AdminNav({ onPick }: { onPick: (t: AdminTab) => void }) {
  return (
    <nav className="amenu">
      {ADMIN_GROUPS.map((g) => (
        <div key={g.title} className="arail__group">
          <div className="arail__title">{g.title}</div>

          {g.items.map((t) => (
            <button
              key={t}
              type="button"
              className="arail__item"
              onClick={() => onPick(t)}
            >
              <span className="arail__label">{ADMIN_TAB_LABEL[t]}</span>
              <span className="arail__hint">{HINT[t]}</span>
              <Icon name="arrow" size={15} />
            </button>
          ))}
        </div>
      ))}
    </nav>
  );
}
